/* eslint-disable max-len */
// @flow
import eye from 'utils/beholder'
import { TAGS } from 'config/constants'
import React, { Fragment, Suspense, lazy } from 'react'
import MediaRouter from 'react-media'
import { Router, Switch, Route, Redirect } from 'react-router-dom'
import { Notification, Page500, Media } from 'easyguide'
import { compose, setPropTypes, defaultProps, lifecycle, withHandlers } from 'recompose'
import { connect } from 'react-redux'
import { func, string, bool, shape } from 'prop-types'
import { AnimatedSwitch } from 'react-router-transition'
import transitions from 'config/transitions'
import browserHistory from 'utils/browserHistory'
import { hideNotification } from 'store/reducers/notification'
import { getFeatureToggle } from 'store/reducers/featureToggle'
import logout from 'services/sidebar'
import { getToken } from 'utils/token'
import getDeepLink from 'utils/deepLink'
import { getPendency } from 'store/reducers/pendency'
import Template from 'components/Template'
import clearLocalStorage from 'utils/localStorage'
import { zeLogout } from 'easycommons/modules/Utils'

const InvestorProfile = lazy(() => import('components/InvestorProfile'))
const PersonalInfo = lazy(() => import('components/PersonalInfo'))
const Sidebar = lazy(() => import('components/Sidebar'))
const TransferSecurity = lazy(() => import('components/TransferSecurity'))
const TransactionHistory = lazy(() => import('components/TransactionHistory'))
// const Account = lazy(() => import('components/Account'))
const AccountStatement = lazy(() => import('components/AccountStatement'))
const AddressInfo = lazy(() => import('components/AddressInfo'))
const NewPassword = lazy(() => import('components/NewPassword'))
const EasyToken = lazy(() => import('components/EasyToken'))
const ElectronicSignature = lazy(() => import('components/ElectronicSignature'))
const BusinessInfo = lazy(() => import('components/BusinessInfo'))
//eslint-disable-next-line
const RecoveryElectronicSignature = lazy(() => import('components/Workflows/RecoveryElectronicSignature/'))
const BrokerTransfer = lazy(() => import('components/BrokerTransfer'))
const CustodyTransferIn = lazy(() => import('components/CustodyTransferIn'))
const CustodyTransfer = lazy(() => import('components/CustodyTransfer'))
const CustodyTransferOut = lazy(() => import('components/CustodyTransferOut'))
const Help = lazy(() => import('components/Help'))
const EducationalManual = lazy(() => import('components/EducationalManual'))
const IncomeReport = lazy(() => import('components/IncomeReport'))
const Irrf = lazy(() => import('components/Irrf'))
const Invoices = lazy(() => import('components/Invoices'))
const FinancialSituation = lazy(() => import('components/FinancialSituation'))
const UpdateRegistry = lazy(() => import('components/UpdateRegistry'))
const Notifications = lazy(() => import('components/Notifications'))
const Earnings = lazy(() => import('components/Earnings'))

const enhancerRoutes = compose(
  connect(
    ({ notification, featureToggle, pendency }) => ({
      notification,
      pendency,
      featureToggle
    }),
    {
      dispatchHideNotification: hideNotification,
      getPendencyConnect: getPendency,
      getFeatureToggle
    }
  ),
  withHandlers({
    makeLogout: () => async () => {
      logout().then(() => {
        zeLogout()
        clearLocalStorage()
        window.location.href = '/'
      }).catch(() => {
        clearLocalStorage()
      })
    }
  }),
  setPropTypes({
    notification: shape({
      hasNotification: bool,
      text: string,
      type: string
    }).isRequired,
    dispatchHideNotification: func.isRequired
  }),
  defaultProps({
    notification: {
      hasNotification: false,
      text: '',
      type: ''
    }
  }),
  lifecycle({
    componentDidMount() {
      this.props.getPendencyConnect()
    }
  })
)

const Routes = enhancerRoutes(
  ({ notification, dispatchHideNotification, featureToggle, pendency, makeLogout }) => {
    const { type, text, hasNotification } = notification

    const allowResidentialUpdate = featureToggle.data
      ? featureToggle.data.some(toggle => toggle === 'inv_1app_sunset_allow_residential_update')
      : false

    const sunsetEarnings = featureToggle.data
      ? featureToggle.data.some(toggle => toggle === 'inv_1app_sunset_earnings_web')
      : false

    const removeEasytoken = true

    return (
      <Router history={browserHistory}>
        <Fragment>
          <Notification
            showNotification={hasNotification}
            type={type}
            hideNotification={dispatchHideNotification}
          >
            {text}
          </Notification>
          <Route
            render={({ history, location: { pathname } }) => {
              if (window.location.pathname === '/configuracoes') {
                eye.shot(TAGS.LOAD_SETTINGS)
              }
              if (window.location.pathname === '/configuracoes/relatorios/proventos') {
                eye.shot(TAGS.EARNINGS.MENU_EARNINGS)
              }
              if (history.action === 'POP') {
                if (!getToken() && process.env.PUBLIC_URL) {
                  const deepLink = getDeepLink(pathname)
                  window.location.replace(`/autenticacao${deepLink}`)
                }
              }
              return (
                <Template pathname={pathname}>
                  <Suspense fallback={<div />}>
                    <MediaRouter query={{ maxWidth: Media.sizes.mobile }}>
                      {screenIsSmall =>
                        screenIsSmall ? (
                          <Fragment>
                            <Switch>
                              <Route
                                exact
                                path="/"
                                render={props => (
                                  <Sidebar
                                    {...props}
                                    pendency={pendency}
                                    isMobile
                                  />
                                )}
                              />
                              <Route
                                path="/perfil/informacoes-pessoais"
                                render={props => <PersonalInfo {...props} isMobile />}
                              />
                              {allowResidentialUpdate && (
                                <Route
                                  path="/perfil/endereco-residencial"
                                  render={props => <AddressInfo {...props} isMobile />}
                                />
                              )}
                              <Route
                                path="/perfil/perfil-investidor"
                                render={props => <InvestorProfile {...props} isMobile />}
                              />
                              <Route
                                path="/perfil/dados-profissionais"
                                render={props => <BusinessInfo {...props} isMobile />}
                              />
                              <Route
                                path="/perfil/situacao-financeira"
                                render={props => <FinancialSituation {...props} isMobile />}
                              />
                              <Route
                                path="/seguranca/senha"
                                render={props => <NewPassword {...props} isMobile />}
                              />
                              <Route
                                path="/seguranca/assinatura-eletronica"
                                render={props => <ElectronicSignature {...props} isMobile />}
                              />
                              {!removeEasytoken && (
                                <Route
                                  path="/seguranca/easytoken"
                                  render={props => <EasyToken {...props} isMobile />}
                                />
                              )}
                              <Route
                                path="/movimentacoes/movimentacao-financeira"
                                render={props => <CustodyTransfer {...props} isMobile />}
                              />
                              <Route
                                path="/movimentacoes/transferencia-de-corretora"
                                render={props => <BrokerTransfer {...props} isMobile />}
                              />
                              <Route
                                exact
                                path="/movimentacoes/transferencia-de-custodia"
                                render={props => <CustodyTransfer {...props} isMobile />}
                              />
                              <Route
                                // eslint-disable-next-line max-len
                                path="/movimentacoes/transferencia-de-custodia/portabilidade-entrada"
                                render={props => <CustodyTransferIn {...props} isMobile />}
                              />
                              <Route
                                path="/movimentacoes/transferencia-de-custodia/portabilidade-saida"
                                render={props => <CustodyTransferOut {...props} isMobile />}
                              />
                              <Route
                                path="/movimentacoes/movimentacao-de-ativos"
                                render={props => <TransferSecurity {...props} isMobile />}
                              />
                              <Route path="/ajuda" render={props => <Help {...props} isMobile />} />
                              <Route
                                path="/atualiza-cadastro"
                                render={props => <UpdateRegistry {...props} isMobile />}
                              />
                              <Route
                                path="/onde-investir"
                                render={props => <EducationalManual {...props} isMobile />}
                              />
                              <Route
                                path="/relatorios/resumo-de-negociacao"
                                render={props => <TransactionHistory {...props} isMobile />}
                              />
                              <Route
                                path="/relatorios/nota-negociacao"
                                render={props => <Invoices {...props} isMobile />}
                              />
                              <Route
                                path="/relatorios/irrf"
                                render={props => <Irrf {...props} isMobile />}
                              />
                              <Route
                                path="/relatorios/extrato"
                                render={props => <AccountStatement {...props} isMobile />}
                              />
                              <Route
                                path="/relatorios/informe-rendimento"
                                render={props => <IncomeReport {...props} isMobile />}
                              />
                              {!sunsetEarnings && (
                                <Route
                                  path="/relatorios/proventos"
                                  render={props => <Earnings {...props} isMobile />}
                                />
                              )}
                              <Route
                                path="/notificacoes"
                                render={props => <Notifications {...props} isMobile />}
                              />
                              <Route
                                path="/sair"
                                render={() => {
                                  makeLogout()
                                }}
                              />
                              <Route
                                path="/erro-de-servico"
                                render={props => (
                                  <Page500
                                    {...props}
                                    isMobile
                                    buttonClickHandler={() => {
                                      props.history.goBack()
                                    }}
                                  />
                                )}
                              />
                              <Route
                                component={() => {
                                  window.location.href = '/pagina-nao-encontrada/'
                                  return null
                                }}
                              />
                            </Switch>
                            <AnimatedSwitch
                              className="route-wrapper"
                              atEnter={transitions.fade.atEnter}
                              atLeave={transitions.fade.atLeave}
                              atActive={transitions.fade.atActive}
                            >
                              <Route
                                path="/seguranca/assinatura-eletronica/recuperar"
                                render={props =>
                                  <RecoveryElectronicSignature {...props} isMobile />
                                }
                              />

                            </AnimatedSwitch>
                          </Fragment>
                        ) : (
                          <Fragment>
                            <Switch>
                              <Redirect exact from="/" to="/relatorios/extrato" />
                              <Redirect exact from="/perfil" to="/perfil/informacoes-pessoais" />
                              <Redirect exact from="/relatorios" to="/relatorios/extrato" />
                              <Redirect exact from="/seguranca" to="/seguranca/senha" />
                              <Redirect
                                exact
                                from="/depositos-e-retiradas"
                                to="/depositos-e-retiradas/conta"
                              />
                              <Redirect
                                exact
                                from="/movimentacoes"
                                to="/movimentacoes/transferencia-de-custodia"
                              />
                              <Route path="/perfil/informacoes-pessoais" component={PersonalInfo} />
                              {allowResidentialUpdate && (
                                <Route
                                  path="/perfil/endereco-residencial"
                                  component={AddressInfo}
                                />
                              )}
                              <Route path="/perfil/perfil-investidor" component={InvestorProfile} />
                              <Route path="/perfil/dados-profissionais" component={BusinessInfo} />
                              <Route
                                path="/perfil/situacao-financeira"
                                component={FinancialSituation}
                              />
                              <Route path="/seguranca/senha" component={NewPassword} />
                              <Route
                                path="/seguranca/assinatura-eletronica"
                                component={ElectronicSignature}
                              />
                              {!removeEasytoken && (
                                <Route path="/seguranca/easytoken" component={EasyToken} />
                              )}
                              <Route
                                path="/movimentacoes/movimentacao-financeira"
                                component={CustodyTransfer}
                              />
                              <Route
                                path="/movimentacoes/transferencia-de-corretora"
                                component={BrokerTransfer}
                              />
                              <Route
                                exact
                                path="/movimentacoes/transferencia-de-custodia"
                                component={CustodyTransfer}
                              />
                              <Route
                                // eslint-disable-next-line max-len
                                path="/movimentacoes/transferencia-de-custodia/portabilidade-entrada"
                                component={CustodyTransferIn}
                              />
                              <Route
                                path="/movimentacoes/transferencia-de-custodia/portabilidade-saida"
                                component={CustodyTransferOut}
                              />
                              <Route
                                path="/movimentacoes/movimentacao-de-ativos"
                                component={TransferSecurity}
                              />
                              <Route path="/ajuda" component={Help} />
                              <Route path="/atualiza-cadastro" component={UpdateRegistry} />
                              <Route path="/onde-investir" component={EducationalManual} />
                              <Route
                                path="/relatorios/resumo-de-negociacao"
                                component={TransactionHistory}
                              />
                              <Route path="/relatorios/nota-negociacao" component={Invoices} />
                              <Route path="/relatorios/irrf" component={Irrf} />
                              <Route path="/relatorios/extrato" component={AccountStatement} />
                              <Route
                                path="/relatorios/informe-rendimento"
                                component={IncomeReport}
                              />
                              <Route path="/relatorios/proventos" component={Earnings} />
                              <Route path="/notificacoes" component={Notifications} />
                              <Route
                                path="/sair"
                                render={() => {
                                  makeLogout()
                                }}
                              />
                              <Route
                                path="/erro-de-servico"
                                component={props => (
                                  <Page500
                                    buttonClickHandler={() => {
                                      props.history.goBack()
                                    }}
                                  />
                                )}
                              />
                              <Route
                                component={() => {
                                  window.location.href = '/pagina-nao-encontrada/'
                                  return null
                                }}
                              />
                              <Redirect
                                exact
                                from="/movimentacoes/transferencia-de-custodia/*"
                                to="/movimentacoes/transferencia-de-custodia"
                              />
                            </Switch>
                            <AnimatedSwitch
                              className="route-wrapper"
                              atEnter={transitions.fade.atEnter}
                              atLeave={transitions.fade.atLeave}
                              atActive={transitions.fade.atActive}
                            >
                              <Route
                                path="/seguranca/assinatura-eletronica/recuperar"
                                render={props => <RecoveryElectronicSignature {...props} />}
                              />

                            </AnimatedSwitch>
                          </Fragment>
                        )
                      }
                    </MediaRouter>
                  </Suspense>
                </Template>
              )
            }}
          />
        </Fragment>
      </Router>
    )
  }
)

export default Routes
